import { useRef, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { m, useScroll, useTransform, useInView } from 'framer-motion';
import { ArrowRight, Phone, ChevronDown, ArrowUpRight, ExternalLink } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import SEO from '../../components/SEO';

/* ─────────────────────────────────────────────────────────────────────────────
   IMAGE MANIFEST
────────────────────────────────────────────────────────────────────────────── */
const IMAGES = {
  hero:        "/hero-villa.webp",
  heroSrcSet:  "/hero-villa-w640.webp 640w, /hero-villa-w1280.webp 1280w, /hero-villa.webp 1920w",
  story:       "/sebastian-lemke.webp",
  storySrcSet: "/sebastian-lemke-w640.webp 640w, /sebastian-lemke.webp 800w",
  partnership: "/solenca-van.webp",
  partnershipSrcSet: "/solenca-van-w640.webp 640w, /solenca-van.webp 800w",
};

/* ─────────────────────────────────────────────────────────────────────────────
   Animation variants
────────────────────────────────────────────────────────────────────────────── */
const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, delay: i * 0.1, ease: [0.16, 1, 0.3, 1] },
  }),
};

/* ─────────────────────────────────────────────────────────────────────────────
   WordReveal — staggered word-by-word animation
────────────────────────────────────────────────────────────────────────────── */
function WordReveal({ text, className, delay = 0 }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-100px' });
  const words = text.split(' ');
  return (
    <span ref={ref} className={className}>
      {words.map((word, i) => (
        <m.span
          key={i}
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5, delay: delay + i * 0.06, ease: [0.16, 1, 0.3, 1] }}
          style={{ display: 'inline-block', marginRight: '0.3em' }}
        >
          {word}
        </m.span>
      ))}
    </span>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
   Counter — animated number on inView
────────────────────────────────────────────────────────────────────────────── */
function Counter({ target, duration = 2 }) {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });
  useEffect(() => {
    if (!inView) return;
    const num = parseInt(target.replace(/\D/g, ''), 10);
    const step = num / (duration * 60);
    let current = 0;
    const timer = setInterval(() => {
      current = Math.min(current + step, num);
      setCount(Math.floor(current));
      if (current >= num) clearInterval(timer);
    }, 1000 / 60);
    return () => clearInterval(timer);
  }, [inView, target, duration]);
  return (
    <span ref={ref}>
      {count}{target.includes('+') ? '+' : target.includes('%') ? '%' : ''}
    </span>
  );
}


/* ═════════════════════════════════════════════════════════════════════════════
   HOME — Main export
═════════════════════════════════════════════════════════════════════════════ */
export default function Home() {
  const heroRef = useRef(null);
  const { t } = useLanguage();

  const { scrollYProgress: heroScroll } = useScroll({ target: heroRef, offset: ['start start', 'end start'] });
  const heroY       = useTransform(heroScroll, [0, 1], ['0%', '30%']);
  const heroOpacity = useTransform(heroScroll, [0, 0.8], [1, 0]);

  return (
    <div className="bg-white overflow-x-hidden">
      <SEO canonical="/" />

      {/* ════════ SECTION 1 — HERO ════════ */}
      <section ref={heroRef} className="relative min-h-screen overflow-hidden flex flex-col justify-center">
        <m.div style={{ y: heroY }} className="absolute inset-0 scale-110">
          <img src={IMAGES.hero} srcSet={IMAGES.heroSrcSet} sizes="100vw" alt="Villa de luxe — Costa Brava" className="w-full h-full object-cover" fetchPriority="high" decoding="sync" />
          <div className="absolute inset-0 bg-black/50" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-black/60" />
        </m.div>

        <m.div style={{ opacity: heroOpacity }} className="relative z-10 max-w-5xl mx-auto px-8 w-full">
          <m.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
            className="text-[#E87722] text-xs font-semibold tracking-[0.4em] uppercase mb-10"
          >
            {t.hero.label}
          </m.p>

          <h1 className="font-heading font-normal leading-[0.88] tracking-[-0.02em] mb-10">
            <span className="block overflow-hidden">
              <m.span
                initial={{ y: '110%', opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 1.1, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
                className="block text-white text-[clamp(3.5rem,9vw,7.5rem)] italic"
              >
                {t.hero.title[0]}
              </m.span>
            </span>
            <span className="block overflow-hidden">
              <m.span
                initial={{ y: '110%', opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 1.1, delay: 0.56, ease: [0.16, 1, 0.3, 1] }}
                className="block text-white text-[clamp(3.5rem,9vw,7.5rem)]"
              >
                {t.hero.title[1]}
              </m.span>
            </span>
          </h1>

          <m.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.85, ease: [0.16, 1, 0.3, 1] }}
            className="text-white/75 text-lg leading-relaxed max-w-md mb-12"
          >
            {t.hero.subtitle}
          </m.p>

          <m.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.05, ease: [0.16, 1, 0.3, 1] }}
            className="flex flex-col sm:flex-row gap-4"
          >
            <Link
              to="/realisations"
              className="group inline-flex items-center justify-center gap-3 bg-white text-[#006039] px-8 py-4 text-[11px] font-bold tracking-[0.22em] uppercase hover:bg-gray-50 transition-all duration-300"
            >
              {t.hero.cta1}
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center gap-3 border border-white/60 text-white px-8 py-4 text-[11px] font-bold tracking-[0.22em] uppercase hover:bg-white/10 hover:border-white transition-all duration-300"
            >
              {t.hero.cta2}
              <ArrowRight className="w-4 h-4" />
            </Link>
          </m.div>
        </m.div>

        <m.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.6 }}
          className="absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 z-10"
        >
          <span className="text-white/50 text-[10px] tracking-[0.35em] uppercase">{t.hero.scroll}</span>
          <m.div animate={{ y: [0, 8, 0] }} transition={{ duration: 1.8, repeat: Infinity, ease: 'easeInOut' }}>
            <ChevronDown className="w-5 h-5 text-white/50" />
          </m.div>
        </m.div>
      </section>

      {/* ════════ SECTION 2 — STORY / FOUNDER ════════ */}
      <section className="bg-white py-32 md:py-40">
        <div className="max-w-6xl mx-auto px-8">
          <div className="grid md:grid-cols-2 gap-16 md:gap-24 items-center">
            <div>
              <m.p
                variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-80px' }}
                className="text-[#E87722] text-[10px] font-bold tracking-[0.4em] uppercase mb-6"
              >
                {t.story.label}
              </m.p>

              <div className="mb-6">
                <WordReveal
                  text={t.story.quote}
                  className="font-heading text-3xl md:text-4xl text-[#006039] italic leading-snug"
                  delay={0.1}
                />
              </div>

              <m.p
                variants={fadeUp} custom={2} initial="hidden" whileInView="visible" viewport={{ once: true }}
                className="text-gray-400 text-sm mb-8"
              >
                {t.story.author}
              </m.p>

              <m.div
                initial={{ scaleX: 0, originX: 0 }}
                whileInView={{ scaleX: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
                className="w-9 h-0.5 bg-[#E87722] mb-10"
              />

              <m.p
                variants={fadeUp} custom={3} initial="hidden" whileInView="visible" viewport={{ once: true }}
                className="text-gray-600 leading-relaxed mb-5 text-base"
              >
                {t.story.p1}
              </m.p>
              <m.p
                variants={fadeUp} custom={4} initial="hidden" whileInView="visible" viewport={{ once: true }}
                className="text-gray-600 leading-relaxed mb-10 text-base"
              >
                {t.story.p2}
              </m.p>

              <m.div variants={fadeUp} custom={5} initial="hidden" whileInView="visible" viewport={{ once: true }}>
                <Link
                  to="/a-propos"
                  className="inline-flex items-center gap-2 text-[#006039] text-sm font-semibold underline underline-offset-4 decoration-[#006039]/30 hover:decoration-[#006039] transition-all"
                >
                  {t.story.cta} <ArrowRight className="w-4 h-4" />
                </Link>
              </m.div>
            </div>

            <m.div
              variants={fadeUp} custom={1} initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-80px' }}
              className="relative"
            >
              <div className="absolute -left-5 top-12 w-0.5 h-20 bg-[#006039]" />
              <div className="relative aspect-[3/4] overflow-hidden">
                <img src={IMAGES.story} srcSet={IMAGES.storySrcSet} sizes="(max-width: 768px) 100vw, 50vw" alt="Sebastian Lemke — Fondateur Construcciones Remalex" className="w-full h-full object-cover" loading="lazy" decoding="async" />
                <div className="absolute bottom-6 left-6 bg-white shadow-2xl p-5">
                  <p className="font-heading text-base text-[#006039] font-semibold leading-tight">Sebastian Lemke</p>
                  <p className="text-gray-400 text-xs mt-1">{t.about.founderRole}</p>
                </div>
              </div>
            </m.div>
          </div>
        </div>
      </section>

      {/* ════════ SECTION 3 — STATS BAR ════════ */}
      <section className="bg-[#006039] border-t-2 border-[#E87722] py-16">
        <div className="max-w-6xl mx-auto px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-0">
            {t.stats.map((s, i) => (
              <m.div
                key={i} custom={i} variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}
                className="text-center px-6 py-4 border-r border-white/10 last:border-r-0"
              >
                <div className="font-heading text-5xl text-white tabular-nums leading-none mb-3">
                  <Counter target={s.value} duration={2} />
                </div>
                <p className="text-white/60 text-xs uppercase tracking-widest leading-snug">{s.label}</p>
              </m.div>
            ))}
          </div>
        </div>
      </section>

      {/* ════════ SECTION 4 — SERVICES ════════ */}
      <section style={{ backgroundColor: '#F8F7F4' }} className="py-24 md:py-32">
        <div className="max-w-5xl mx-auto px-8">
          {/* Header */}
          <div className="mb-16 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <div>
              <m.p
                variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}
                className="text-[#E87722] text-[10px] font-bold tracking-[0.4em] uppercase mb-5"
              >
                {t.services.label}
              </m.p>
              <m.h2
                variants={fadeUp} custom={1} initial="hidden" whileInView="visible" viewport={{ once: true }}
                className="font-heading text-4xl md:text-5xl lg:text-6xl text-[#1A1A1A] tracking-tight leading-tight"
              >
                {t.services.title}
              </m.h2>
            </div>
            <m.div variants={fadeUp} custom={2} initial="hidden" whileInView="visible" viewport={{ once: true }}>
              <Link
                to="/services"
                className="inline-flex items-center gap-2 text-gray-400 text-[10px] font-bold tracking-[0.25em] uppercase hover:text-[#006039] transition-colors"
              >
                {t.services.viewAll} <ArrowUpRight className="w-3.5 h-3.5" />
              </Link>
            </m.div>
          </div>

          {/* Services — numbered list */}
          <div className="divide-y divide-[#1A1A1A]/[0.07]">
            {t.services.items.map((s, i) => (
              <m.div
                key={i}
                custom={i}
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: '-40px' }}
              >
                <Link
                  to={s.link}
                  className="group flex items-center gap-6 md:gap-10 py-7 hover:pl-2 transition-all duration-300"
                >
                  {/* Number */}
                  <span className="font-heading text-[#006039]/20 text-2xl w-10 shrink-0 tabular-nums">
                    {String(i + 1).padStart(2, '0')}
                  </span>

                  {/* Title */}
                  <h3 className="font-heading text-xl md:text-2xl lg:text-3xl text-[#1A1A1A] tracking-tight flex-1 group-hover:text-[#006039] transition-colors duration-300">
                    {s.name}
                  </h3>

                  {/* Desc — hidden on mobile, visible md+ */}
                  <p className="hidden md:block text-[#1A1A1A]/45 text-sm leading-relaxed max-w-xs flex-1">
                    {s.desc}
                  </p>

                  {/* Arrow */}
                  <span className="shrink-0 w-8 h-8 rounded-full border border-[#006039]/20 flex items-center justify-center group-hover:bg-[#006039] group-hover:border-[#006039] transition-all duration-300">
                    <ArrowUpRight className="w-3.5 h-3.5 text-[#006039] group-hover:text-white transition-colors duration-300" />
                  </span>
                </Link>
              </m.div>
            ))}
          </div>
        </div>
      </section>

      {/* ════════ SECTION 5 — PARTNERSHIP SOLENCA ════════ */}
      <section className="bg-white py-32">
        <div className="max-w-6xl mx-auto px-8">
          <div className="grid md:grid-cols-2 gap-16 md:gap-24 items-center">
            <div>
              <m.p
                variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}
                className="text-[#E87722] text-[10px] font-bold tracking-[0.4em] uppercase mb-6"
              >
                {t.partnership.label}
              </m.p>
              <m.h2
                variants={fadeUp} custom={1} initial="hidden" whileInView="visible" viewport={{ once: true }}
                className="font-heading text-4xl md:text-5xl text-[#1A1A1A] tracking-tight leading-tight mb-6"
              >
                {t.partnership.title}
              </m.h2>
              <m.p
                variants={fadeUp} custom={2} initial="hidden" whileInView="visible" viewport={{ once: true }}
                className="text-gray-600 leading-relaxed mb-10 text-base"
              >
                {t.partnership.desc}
              </m.p>
              <m.div
                variants={fadeUp} custom={3} initial="hidden" whileInView="visible" viewport={{ once: true }}
                className="flex items-center gap-4 mb-10"
              >
                <span className="bg-[#006039] text-white text-xs font-bold tracking-widest uppercase px-4 py-2">
                  {t.partnership.tag1}
                </span>
                <span className="text-[#E87722] font-heading text-xl">+</span>
                <span className="border border-[#006039] text-[#006039] text-xs font-bold tracking-widest uppercase px-4 py-2">
                  Solenca
                </span>
              </m.div>
              <m.div variants={fadeUp} custom={4} initial="hidden" whileInView="visible" viewport={{ once: true }}>
                <a
                  href="https://www.solenca.es"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-[#006039] text-sm font-semibold underline underline-offset-4 decoration-[#006039]/30 hover:decoration-[#006039] transition-all"
                >
                  {t.partnership.cta} <ExternalLink className="w-4 h-4" />
                </a>
              </m.div>
            </div>

            <m.div
              variants={fadeUp} custom={1} initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-80px' }}
              className="relative"
            >
              <div className="relative aspect-[4/3] overflow-hidden">
                <img src={IMAGES.partnership} srcSet={IMAGES.partnershipSrcSet} sizes="(max-width: 768px) 100vw, 50vw" alt="Partenariat Solenca — gestion immobilière Costa Brava" className="w-full h-full object-cover" loading="lazy" decoding="async" />
                <div className="absolute bottom-6 right-6 bg-[#006039] text-white p-6 max-w-[220px] shadow-2xl">
                  <p className="text-[#E87722] text-[10px] font-bold tracking-widest uppercase mb-2">{t.partnership.label}</p>
                  <p className="font-heading text-lg leading-snug">{t.partnership.tag1} + {t.partnership.tag2}</p>
                </div>
              </div>
            </m.div>
          </div>
        </div>
      </section>

      {/* ════════ SECTION 6 — TESTIMONIAL ════════ */}
      <section style={{ backgroundColor: '#EFF5F1' }} className="py-24">
        <div className="max-w-4xl mx-auto px-8 text-center">
          <m.div
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
            className="font-heading text-8xl text-[#E87722] leading-none mb-6 select-none"
            aria-hidden="true"
          >
            "
          </m.div>

          <div className="font-heading text-2xl md:text-3xl text-[#1A1A1A] italic leading-relaxed mb-8">
            <WordReveal
              text={t.testimonial.quote}
              delay={0.05}
            />
          </div>

          <m.p
            variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}
            className="text-gray-400 text-xs uppercase tracking-widest mb-4"
          >
            {t.testimonial.author} — {t.testimonial.location}
          </m.p>
          <m.div
            variants={fadeUp} custom={1} initial="hidden" whileInView="visible" viewport={{ once: true }}
            className="text-[#E87722] text-xl tracking-wider"
          >
            ★★★★★
          </m.div>
        </div>
      </section>

      {/* ════════ SECTION 7 — FINAL CTA ════════ */}
      <section style={{ backgroundColor: '#003A22' }} className="py-32 text-center relative overflow-hidden">
        <div aria-hidden="true" className="absolute inset-0 flex items-center justify-center pointer-events-none select-none overflow-hidden">
          <span className="font-heading text-[22vw] text-white/[0.03] leading-none whitespace-nowrap">Remalex</span>
        </div>

        <div className="relative z-10 max-w-4xl mx-auto px-8">
          <m.h2
            variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}
            className="font-heading text-5xl md:text-7xl text-white tracking-tight leading-tight mb-6"
          >
            {t.cta.title}
          </m.h2>

          <m.p
            variants={fadeUp} custom={1} initial="hidden" whileInView="visible" viewport={{ once: true }}
            className="text-white/60 text-lg leading-relaxed max-w-lg mx-auto mb-12"
          >
            {t.cta.subtitle}
          </m.p>

          <m.div
            variants={fadeUp} custom={2} initial="hidden" whileInView="visible" viewport={{ once: true }}
            className="flex flex-col sm:flex-row gap-4 justify-center"
          >
            <Link
              to="/contact"
              className="group inline-flex items-center justify-center gap-3 bg-white text-[#006039] px-10 py-4 text-[11px] font-bold tracking-[0.22em] uppercase hover:bg-gray-50 transition-colors duration-300"
            >
              {t.cta.btn1}
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
            </Link>
            <a
              href="tel:+34602645005"
              className="inline-flex items-center justify-center gap-3 border border-white/40 text-white px-10 py-4 text-[11px] font-bold tracking-[0.22em] uppercase hover:bg-white/10 hover:border-white transition-all duration-300"
            >
              <Phone className="w-4 h-4" />
              {t.cta.btn2}
            </a>
          </m.div>

          <m.p
            variants={fadeUp} custom={3} initial="hidden" whileInView="visible" viewport={{ once: true }}
            className="text-white/30 text-xs tracking-[0.45em] uppercase mt-16"
          >
            {t.hero.label}
          </m.p>
        </div>
      </section>

    </div>
  );
}
